import React, { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';

type OperatorStatus = 'available' | 'busy' | 'away' | 'offline';

interface Operator {
  id: string;
  displayName: string;
  status: OperatorStatus;
  currentWorkload: number;
  maxWorkload: number;
}

interface Assignment {
  id: string;
  assigneeId?: string;
  entity?: {
    title?: string;
  };
}

interface ReassignData {
  assignmentId: string;
  newAssigneeId: string;
  notes?: string;
}

interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReassign?: (data: ReassignData) => void;
  assignment?: Assignment;
  operators?: Operator[];
  isLoading?: boolean;
  brand?: BrandConfig;
}

const ReassignModal: React.FC<ReassignModalProps> = ({
  isOpen,
  onClose,
  onReassign,
  assignment,
  operators = [],
  isLoading = false,
  brand,
}) => {
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [notes, setNotes] = useState('');

  // Reset form when modal opens
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- reset form state on modal open */
  useEffect(() => {
    if (isOpen) {
      setSelectedOperatorId('');
      setNotes('');
    }
  }, [isOpen]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (selectedOperatorId && onReassign && assignment) {
      onReassign({
        assignmentId: assignment.id,
        newAssigneeId: selectedOperatorId,
        notes: notes.trim() || undefined,
      });
    }
  };

  // Filter to available operators (exclude current assignee and offline operators)
  const availableOperators = operators.filter(
    op => op.id !== assignment?.assigneeId && op.status !== 'offline'
  );

  const getStatusColor = (status: OperatorStatus): string => {
    switch (status) {
      case 'available': return '#10B981';
      case 'busy': return '#F59E0B';
      case 'away': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getWorkloadColor = (current: number, max: number): string => {
    const ratio = current / max;
    if (ratio >= 1) return brand?.colors?.error || '#EF4444';
    if (ratio >= 0.8) return brand?.colors?.warning || '#F59E0B';
    return brand?.colors?.success || '#10B981';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-modal-title"
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b"
          style={{ borderColor: brand?.colors?.border || '#E5E7EB' }}
        >
          <div className="flex items-center justify-between">
            <h2
              id="reassign-modal-title"
              className="text-lg font-semibold"
              style={{ color: brand?.colors?.secondary || '#1F2937' }}
            >
              Reassign Work Item
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {assignment?.entity?.title && (
            <p
              className="text-sm mt-1"
              style={{ color: brand?.colors?.textLight || '#6B7280' }}
            >
              {assignment.entity.title}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Operator Selection */}
          <ChoiceGroup question="Select Operator" className="mb-4" legendClassName="mb-2">
            {availableOperators.length === 0 ? (
              <p
                className="text-sm py-4 text-center rounded-lg"
                style={{ backgroundColor: brand?.colors?.bgLight || '#F9FAFB', color: brand?.colors?.textLight }}
              >
                No available operators found
              </p>
            ) : (
              <div
                className="max-h-48 overflow-y-auto rounded-lg border"
                style={{ borderColor: brand?.colors?.border || '#E5E7EB' }}
              >
                {availableOperators.map((operator) => (
                  <label
                    key={operator.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                      selectedOperatorId === operator.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    style={{
                      borderBottom: `1px solid ${brand?.colors?.border || '#E5E7EB'}`,
                    }}
                  >
                    <input
                      type="radio"
                      name="operator"
                      value={operator.id}
                      checked={selectedOperatorId === operator.id}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedOperatorId(e.target.value)}
                      className="w-4 h-4"
                      style={{ accentColor: brand?.colors?.primary || '#2563EB' }}
                      aria-label={operator.displayName}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: getStatusColor(operator.status) }}
                        />
                        <span
                          className="text-sm font-medium truncate"
                          style={{ color: brand?.colors?.text || '#374151' }}
                        >
                          {operator.displayName}
                        </span>
                      </div>
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: brand?.colors?.textLight || '#6B7280' }}
                      >
                        Workload: <span style={{ color: getWorkloadColor(operator.currentWorkload, operator.maxWorkload) }}>
                          {operator.currentWorkload}/{operator.maxWorkload}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </ChoiceGroup>

          {/* Notes */}
          <div className="mb-6">
            <label
              htmlFor="reassign-notes"
              className="block text-sm font-medium mb-2"
              style={{ color: brand?.colors?.text || '#374151' }}
            >
              Notes (optional)
            </label>
            <textarea
              id="reassign-notes"
              value={notes}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              placeholder="Add a note about this reassignment..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{
                borderColor: brand?.colors?.border || '#E5E7EB',
                color: brand?.colors?.text || '#374151',
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:bg-gray-50"
              style={{
                borderColor: brand?.colors?.border || '#E5E7EB',
                color: brand?.colors?.text || '#374151',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedOperatorId || isLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: brand?.colors?.primary || '#2563EB',
                color: brand?.colors?.onPrimary || 'white',
              }}
            >
              {isLoading ? 'Reassigning...' : 'Reassign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReassignModal;
